import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import getLPCRegionLOS from 'lib/offer/getLPCRegionLOS'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import BodyText from 'LuxKit/Typography/BodyText'
import { useAppSelector } from 'hooks/reduxHooks'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPriceNew'
import useOfferList from 'hooks/Offers/useOfferList'
import { getDefaultAirport, getDefaultAirportCode } from 'selectors/flightsSelectors'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import getOfferDurationText from 'lib/offer/getOfferDurationText'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import { EmptyObject } from 'lib/object/objectUtils'
import { isFlashHotelOffer, isHotelPackage } from 'lib/offer/offerTypes'
import { useMaldivesDirectBundleContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectBundleContext'
import { buildBundleOfferFilters } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectUtils'
import { useDirectSearchPrices } from 'hooks/Search/useSearchPrices'
import { getCharterBundleRoute } from 'selectors/offerPage/initialOfferPageStateSelectors'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { FlightsClassTypes } from 'constants/flight'
import { MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import { getMaldivesDirectSearchOriginAirportCode } from 'lib/charterFlights/charterBundleOfferPageUtils'
import { resolveAllowedFareClass } from 'lib/charterFlights/charterOfferFareClassRestrictions'
import CharterBundlePricing from 'components/SearchKit/SearchTiles/accommodations/CharterBundlePricing'
import { getCharterBundleSearchTilePricing } from 'components/SearchKit/SearchTiles/accommodations/charterBundleSearchTilePricing'
import { isDisplayablePrice } from 'lib/offer/pricingVisibility'
import { computeRewardBundlePrice, REWARD_PREVIEW_RATE_BY_CABIN } from 'lib/maldivesCharter/classicRewardPricing'
import resolveLuxPlusMemberPrice from 'lib/search/resolveLuxPlusMemberPrice'
import Places from 'constants/places'
import config from 'constants/config'
import RewardBundlePriceBlock from './RewardBundlePriceBlock'

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary | App.TourOffer | App.TourOfferSummary | App.VillaOffer | App.VillaOfferSummary
  selectedDatesAndRoomsFilter?: App.OfferListFilters
}

const DEFAULT_ROOMS: Array<App.Occupants> = [config.search.defaultOccupants]

function LEOfferForceBundlePricing(props: Props) {
  const {
    offer,
    selectedDatesAndRoomsFilter = EmptyObject,
  } = props
  const intl = useIntl()
  const perPersonSaleUnit = intl.formatMessage({
    id: '9puVBD3Kp2L5L-AmoMVK',
    defaultMessage: 'per person',
    description: 'Sale unit label for pricing that is shown on a per-person basis in offer pricing',
  })

  const bestPkg = useOffersBestPrices(offer, selectedDatesAndRoomsFilter)
  const pkg = bestPkg?.bestPricePackage ?? offer.lowestPricePackage!

  const {
    charterBundle: charterBundleFromContext,
    isPricePerPerson,
    fareClass,
    useRewardPreviewPricing = false,
  } = useMaldivesDirectBundleContext()
  const rooms = selectedDatesAndRoomsFilter.rooms?.length
    ? selectedDatesAndRoomsFilter.rooms
    : DEFAULT_ROOMS
  const totalPassengers = useMemo(() => sumUpOccupancies(rooms), [rooms])
  const charterBundleRoute = useAppSelector(state => getCharterBundleRoute(state, offer))
  const defaultAirport = useAppSelector(getDefaultAirport)

  const isCharterBundleOffer = useMemo(() => {
    return isFlashHotelOffer(offer) &&
      offer.offerFlightsEnabled &&
      offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
  }, [offer])
  const isCharterBundle = !!(charterBundleFromContext || isCharterBundleOffer || charterBundleRoute)
  const shouldShowPerPersonPricing = charterBundleFromContext && isPricePerPerson

  const previewFareClass = useMemo(() => {
    const selectedFareClass = fareClass ?? FlightsClassTypes.ECONOMY
    return resolveAllowedFareClass({
      selectedFareClass,
      campaignTags: offer.campaignTags,
      occupancies: rooms,
    }) ?? selectedFareClass
  }, [fareClass, offer.campaignTags, rooms])

  // Cash charter totals come from stream search. Société reward preview uses package hotel
  // + indicative rates and must not depend on stream metadata.
  const charterPricingFilters = useMemo<App.OfferListFilters | undefined>(() => {
    if (!isCharterBundle || useRewardPreviewPricing) return undefined
    if (selectedDatesAndRoomsFilter.charterBundle) {
      return selectedDatesAndRoomsFilter
    }

    return buildBundleOfferFilters({
      destinationId: Places.Maldives.id,
      destinationName: Places.Maldives.name,
      checkIn: selectedDatesAndRoomsFilter.checkIn ?? '',
      checkOut: selectedDatesAndRoomsFilter.checkOut ?? '',
      rooms,
      fareClass: (selectedDatesAndRoomsFilter.fareClass ?? FlightsClassTypes.ECONOMY) as App.OfferListFilters['fareClass'],
      feederOriginAirportCode: selectedDatesAndRoomsFilter.feederOriginAirportCode
        ?? getMaldivesDirectSearchOriginAirportCode(defaultAirport),
    })
  }, [isCharterBundle, useRewardPreviewPricing, selectedDatesAndRoomsFilter, rooms, defaultAirport])

  const charterOfferList = useOfferList(charterPricingFilters, {
    disabled: !charterPricingFilters,
    minRequiredOffers: 1,
  })

  const directSearchPrices = useDirectSearchPrices({
    filters: charterPricingFilters ?? EmptyObject,
    offerId: offer.id,
  })
  const streamSearchPricing = useMemo(
    () => getCharterBundleSearchTilePricing({ charterSearchPrices: directSearchPrices }),
    [directSearchPrices],
  )

  // Non-charter force-bundle (bundledWithFlightsOnly): still use offer-cached flight prices.
  const flightOrigin = useAppSelector(state => getDefaultAirportCode(state))
  const flightPrice = offer.flightPrices[flightOrigin || '']
  const {
    withFlights: pricingWithFlights,
  } = useOfferPackagePrice(pkg, offer, { flightPrice })

  const charterNightsAndFlightsCaption = intl.formatMessage(
    {
      id: 'gEFgViLaxzHaUoh9q6IV',
      defaultMessage: '{count, plural, one {# night} other {# nights}} & flights from',
      description: 'Bundle offer duration in nights with flights, shown as price caption on offer card',
    },
    { count: pkg.duration },
  )
  const priceUnavailableLabel = intl.formatMessage({
    id: 'K30iQ4V8k1Y_PQeTyOMV',
    defaultMessage: 'Price unavailable',
    description: 'Shown on charter force-bundle offer cards when stream search finished without a displayable price for this offer',
  })

  const rewardPreviewPriceElement = useMemo(() => {
    if (!useRewardPreviewPricing || !isCharterBundle) return null

    const previewRewardRate = REWARD_PREVIEW_RATE_BY_CABIN[previewFareClass]
    if (!previewRewardRate) return null

    const roomMultiplier = rooms.length || 1
    const packageHotelTotal = (pkg.price ?? 0) * roomMultiplier
    if (packageHotelTotal <= 0) return null

    const packageMemberHotelTotal = (pkg.memberPrice ?? 0) * roomMultiplier
    // resolveLuxPlusMemberPrice works at single-package scale; scale the result to rooms
    // so member totals stay aligned with packageHotelTotal / packageMemberHotelTotal.
    const resolvedPerPackageMemberPrice = resolveLuxPlusMemberPrice({
      metadataLowestMemberPrice: undefined,
      referencePrice: pkg.price,
      packagePrice: pkg.price,
      packageMemberPrice: pkg.memberPrice,
      fallbackMemberPrice: pkg.memberPrice,
    })
    const hotelTotalMemberPrice = resolvedPerPackageMemberPrice != null
      ? resolvedPerPackageMemberPrice * roomMultiplier
      : (packageMemberHotelTotal > 0 ? packageMemberHotelTotal : undefined)

    const rewardPrice = computeRewardBundlePrice({
      hotelPrice: packageHotelTotal,
      hotelMemberPrice: hotelTotalMemberPrice,
      rewardPointsTotal: previewRewardRate.pointsPerPerson * totalPassengers,
      rewardCashCoPayTotal: previewRewardRate.cashPerPerson * totalPassengers,
    })
    const divisor = shouldShowPerPersonPricing && totalPassengers > 0 ? totalPassengers : 1
    const isMemberReward = rewardPrice.memberCash !== undefined
    const rewardPoints = rewardPrice.points / divisor
    const rewardCashTotal = (rewardPrice.memberCash ?? rewardPrice.cash) / divisor
    const nonMemberCashTotal = rewardPrice.cash / divisor
    const saleUnit = shouldShowPerPersonPricing ? perPersonSaleUnit : ''

    return <RewardBundlePriceBlock
      points={rewardPoints}
      cash={rewardCashTotal}
      nonMemberCash={nonMemberCashTotal}
      isMemberReward={isMemberReward}
      saleUnit={saleUnit}
      saleUnitDisplay="slash"
      nonMemberDisplay="bold"
      testId="main-offer-pricing-reward-preview"
      showLuxPlusLogo
    />
  }, [
    useRewardPreviewPricing,
    isCharterBundle,
    previewFareClass,
    rooms.length,
    pkg.price,
    pkg.memberPrice,
    totalPassengers,
    shouldShowPerPersonPricing,
    perPersonSaleUnit,
  ])

  if (useRewardPreviewPricing && isCharterBundle) {
    if (!rewardPreviewPriceElement) {
      return null
    }

    return <>
      <PriceRowPriceCaption>{charterNightsAndFlightsCaption}</PriceRowPriceCaption>
      {rewardPreviewPriceElement}
    </>
  }

  if (isCharterBundle) {
    if (!streamSearchPricing || !isDisplayablePrice(streamSearchPricing.price) || !charterPricingFilters) {
      // Do not skeleton-wait on an open anytime catalogue stream: Trip Planner and other
      // foreign offer IDs can sit in `listening` for a long time before (or without) this
      // offer appearing. Match pre-skeleton behaviour — render nothing until metadata
      // arrives; only show unavailable once the stream has actually ended.
      if (charterPricingFilters && charterOfferList.streamEnded) {
        return <>
          <PriceRowPriceCaption>{charterNightsAndFlightsCaption}</PriceRowPriceCaption>
          <BodyText
            variant="medium"
            colour="neutral-two"
            data-testid="charter-bundle-price-unavailable"
          >
            {priceUnavailableLabel}
          </BodyText>
        </>
      }
      return null
    }

    return <CharterBundlePricing
      offer={offer}
      pricing={{
        ...streamSearchPricing,
        totalGuests: totalPassengers,
        roomCount: rooms.length,
      }}
      isPricePerPerson={shouldShowPerPersonPricing}
      nights={directSearchPrices?.duration ?? pkg.duration}
      filters={charterPricingFilters}
      hideIncludesTaxesAndFees
    />
  }

  if (!pricingWithFlights) {
    return null
  }

  const totalPriceWithFlights = pricingWithFlights.price
  const displayTotalValue = pricingWithFlights.value
  const discountPercent = pricingWithFlights.discountPercent

  const shouldShowRate = !offer.isDiscountPillHidden && discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const shouldShowValue = pkg.value > 0 && totalPriceWithFlights && displayTotalValue > totalPriceWithFlights && pkg.shouldDisplayValue &&
    (isFlashHotelOffer(offer) || (isHotelPackage(pkg) && !pkg.roomRate?.inclusionsHideValue))

  let staysFrom = offer.minDuration
  const LPCRegionLOS = getLPCRegionLOS(offer)
  if (LPCRegionLOS && offer.packages.some(p => p.duration === LPCRegionLOS)) {
    staysFrom = LPCRegionLOS
  }
  const showStaysFromLabel = staysFrom && offer.type === OFFER_TYPE_ALWAYS_ON && LPCRegionLOS && staysFrom < pkg.duration

  const durationText = getOfferDurationText(intl, offer, pkg)

  return (<>
    <PriceRowPriceCaption>{durationText}</PriceRowPriceCaption>
    <PriceRowPrice
      price={totalPriceWithFlights}
      saleUnit={pricingWithFlights.saleUnit}
      size="L"
    />
    {shouldShowValue && <PriceRowValueDiscountWithCaption
      size="L"
      originalValue={Math.ceil(displayTotalValue)}
      discountPercentage={shouldShowRate ? discountPercent : undefined}
    />}
    {staysFrom && showStaysFromLabel && <PriceRowCaption>
      <FormattedMessage
        id="7PFPr3tSopW8Hgmmb_Dy"
        defaultMessage="Stays from {staysFrom, plural, one {# night} other {# nights}} available"
        description="Caption indicating minimum stay duration available for an always-on offer"
        values={{ staysFrom }}
      />
    </PriceRowCaption>}
    {!!pkg.roomOccupancy && offer.type === 'tour' && <PriceRowCaption>
      {pkg.roomOccupancy}
    </PriceRowCaption>}
  </>)
}

export default LEOfferForceBundlePricing
